import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminAuth from '../components/admin/AdminAuth';
import BookingCard from '../components/admin/BookingCard';
import {
  getCalendarBookings,
  getCalendarSyncState,
  callSyncCalendarBookings,
  getConvertedLeads
} from '../services/contentOpsService';

const formatDate = (ts) => {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const CalendarAdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [bookings, setBookings] = useState([]);
  const [convertedLeads, setConvertedLeads] = useState([]);
  const [syncState, setSyncState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [upcoming, past, unlinked, converted, state] = await Promise.all([
        getCalendarBookings('upcoming', 50),
        getCalendarBookings('past', 50),
        getCalendarBookings('unlinked', 50),
        getConvertedLeads(50),
        getCalendarSyncState()
      ]);

      // Store all bookings in state, filter by tab
      setBookings({ upcoming, past, unlinked, all: [...upcoming, ...past] });
      setConvertedLeads(converted);
      setSyncState(state);
    } catch (err) {
      console.error('Failed to load calendar data:', err);
      setError('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setError('');
      const result = await callSyncCalendarBookings(30, 60);
      alert(`Synced! Created: ${result.created}, Updated: ${result.updated}, Linked: ${result.linked}`);
      await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
      setError('Sync failed: ' + (err.message || 'Unknown error'));
    } finally {
      setSyncing(false);
    }
  };

  const tabs = [
    { key: 'upcoming', label: `Upcoming (${bookings.upcoming?.length || 0})` },
    { key: 'past', label: `Past (${bookings.past?.length || 0})` },
    { key: 'unlinked', label: `Unlinked (${bookings.unlinked?.length || 0})` },
    { key: 'conversions', label: `Conversions (${convertedLeads.length})` }
  ];

  const currentBookings = activeTab === 'conversions' ? [] : (bookings[activeTab] || []);

  return (
    <AdminAuth>
      <div className="min-h-screen bg-gray-900 text-white">
        {/* Header */}
        <div className="bg-gray-800 border-b border-gray-700">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/admin')}
                  className="text-gray-400 hover:text-white"
                >
                  ← Back
                </button>
                <h1 className="text-xl font-bold">Calendar & Bookings</h1>
              </div>
              <div className="flex items-center gap-4">
                {syncState && (
                  <span className="text-sm text-gray-400">
                    Last sync: {formatDate(syncState.lastSyncAt)}
                  </span>
                )}
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="max-w-6xl mx-auto px-4 py-2">
            <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-2 rounded flex justify-between items-center">
              <span>{error}</span>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-200">×</button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex gap-2 border-b border-gray-700 mb-4">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-white'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : activeTab === 'conversions' ? (
            /* Conversions Tab - Show converted leads */
            <div className="space-y-3">
              {convertedLeads.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No conversions yet. When leads book calls, they'll appear here.
                </div>
              ) : (
                convertedLeads.map(lead => (
                  <div key={lead.id} className="bg-gray-800 rounded-lg border border-emerald-600/30 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-white font-medium">
                          {lead.name || lead.widgetTitle}
                        </h3>
                        {lead.contactEmail && (
                          <p className="text-sm text-gray-400">{lead.contactEmail}</p>
                        )}
                        {lead.link && (
                          <a
                            href={lead.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-400 hover:underline"
                          >
                            {lead.link}
                          </a>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-xs px-2 py-1 rounded-full bg-emerald-600/20 text-emerald-400">
                          Converted
                        </span>
                        {lead.convertedAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(lead.convertedAt)}
                          </p>
                        )}
                        {lead.conversionSource && (
                          <p className="text-xs text-gray-500">
                            via {lead.conversionSource}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Bookings Tabs */
            <div className="space-y-3">
              {currentBookings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {activeTab === 'upcoming' && 'No upcoming bookings. Calendar events will appear here after sync.'}
                  {activeTab === 'past' && 'No past bookings found.'}
                  {activeTab === 'unlinked' && 'All bookings are linked to leads. Great job!'}
                </div>
              ) : (
                currentBookings.map(booking => (
                  <BookingCard
                    key={booking.id}
                    booking={booking}
                    onUpdate={loadData}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer Stats */}
        {syncState && (
          <div className="max-w-6xl mx-auto px-4 py-4 border-t border-gray-800">
            <div className="text-xs text-gray-500">
              Total events fetched: {syncState.totalEventsFetched || 0}
              {syncState.lastError && (
                <span className="text-red-400 ml-4">Last error: {syncState.lastError}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminAuth>
  );
};

export default CalendarAdminPage;
